import { useState, useMemo } from 'react';
import { useLiveProductsQuery } from './useProductsQuery';
import { useQuote } from '@features/quote/context/QuoteContext';
import { useDebounce } from '@shared/hooks/useDebounce';
import { deriveVehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';
import type { Product, RejectionReason } from '../types/products.types';

export const useProductSearchWithQuery = () => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [view, setView] = useState<'carousel' | 'table'>('carousel');

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const { selectedVehicle, selectedDealer } = useQuote();

    const vehicleParams = useMemo(() => {
        if (!selectedVehicle) {
            return {};
        }

        const derivative =
            'derivative' in selectedVehicle && selectedVehicle.derivative
                ? selectedVehicle.derivative
                : selectedVehicle;

        if ('make' in derivative && 'model' in derivative) {
            const makeId = derivative.make?.id ?? undefined;
            const scheduleContext = deriveVehicleScheduleContext(selectedVehicle);
            return {
                make_id: makeId,
                model_id: derivative.model?.id ?? undefined,
                derivative_id: derivative.id ?? undefined,
                ...(derivative.transmission?.id ? { transmission_id: derivative.transmission.id } : {}),
                ...(derivative.fuel_type?.id ? { fuel_type_id: derivative.fuel_type.id } : {}),
                ...(makeId && scheduleContext.engineSize !== undefined
                    ? { engine_size: scheduleContext.engineSize }
                    : {}),
                ...(scheduleContext.is4x4 ? { is_4x4: true } : {}),
            };
        }

        return {};
    }, [selectedVehicle]);

    const vehicleEligibilityParams = useMemo(() => {
        // Shared with useDerivedSchedule (Epic 2.4.2 Slice B) — see
        // deriveVehicleScheduleContext's doc comment for why this is extracted.
        const context = deriveVehicleScheduleContext(selectedVehicle);

        const result: {
            vehicle_age_years?: number;
            vehicle_mileage?: number;
            vehicle_fuel_type?: string;
        } = {};

        if (context.vehicleAgeYears !== undefined) {
            result.vehicle_age_years = context.vehicleAgeYears;
        }
        if (context.vehicleMileage !== undefined) {
            result.vehicle_mileage = context.vehicleMileage;
        }
        if (context.vehicleFuelType !== undefined) {
            result.vehicle_fuel_type = context.vehicleFuelType;
        }

        return result;
    }, [selectedVehicle]);

    const dealerParam = useMemo(
        () => (selectedDealer?.id ? { dealer_id: selectedDealer.id } : {}),
        [selectedDealer],
    );

    const queryParams = useMemo(() => {
        // When we have vehicle/dealer eligibility context, ask the backend to annotate
        // each product with its eligibility_result so ProductCard/ProductTable can render
        // the eligibility UI. Without this flag the backend omits the field entirely.
        const hasEligibilityContext =
            Object.keys(vehicleEligibilityParams).length > 0 || Object.keys(dealerParam).length > 0;

        return {
            ...vehicleParams,
            ...vehicleEligibilityParams,
            ...dealerParam,
            ...(hasEligibilityContext && { include_eligibility_reasons: true }),
            ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
            capability: 'active_for_sales' as const,
        };
    }, [vehicleParams, vehicleEligibilityParams, dealerParam, debouncedSearchTerm]);

    const {
        data,
        isFetchedAfterMount,
        isError,
        error,
        refetch,
        isFetching,
    } = useLiveProductsQuery(queryParams);

    const page = isFetchedAfterMount && !isError ? data : undefined;
    const products: Product[] = page?.data ?? [];
    const rejectionReasons: RejectionReason[] = page?.meta?.rejection_reasons ?? [];

    const selectProduct = (product: Product) => {
        setSelectedProduct(product);
    };

    const clearSelection = () => {
        setSelectedProduct(null);
    };

    const resetSearch = () => {
        setSearchTerm('');
        setSelectedProduct(null);
        refetch();
    };

    const toggleView = () => {
        setView((prev) => (prev === 'carousel' ? 'table' : 'carousel'));
    };

    return {
        products,
        rejectionReasons,
        searchTerm,
        debouncedSearchTerm,
        setSearchTerm,
        selectedProduct,
        selectProduct,
        clearSelection,
        resetSearch,
        refetch,
        view,
        toggleView,
        isLoading: !isFetchedAfterMount,
        isFetching,
        error,
        totalProducts: products.length,
        filteredCount: products.length,
        selectedVehicle,
        vehicleParams,
    };
};
